/** app.dustlark.app — every state, streamed. The shell mirrors the phone's
 * map HUD (state pill, look cycle, layers) in First Light. State, look and
 * camera live in the URL hash: every view is a link. */

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type maplibregl from "maplibre-gl";
import { type CameraState } from "./map/MapCanvas";
import {
  bboxCenter,
  buildHash,
  fetchManifest,
  fetchStates,
  layerURL,
  parseHash,
  stateContaining,
  type StateEntry,
  type StateManifest,
} from "./catalog";
import {
  availableLooks,
  DEFAULT_TOGGLES,
  LOOK_LABELS,
  nextLook,
  type LayerToggles,
  type MapLook,
} from "./map/firstlight";
import { solarPosition, type SunPosition } from "./map/solar";
import LayersPanel from "./ui/LayersPanel";
import LookMenu from "./ui/LookMenu";
import StatePicker from "./ui/StatePicker";
import SearchPalette from "./ui/SearchPalette";
import PlanBar from "./ui/PlanBar";
import ProfileStrip from "./ui/ProfileStrip";
import PlansSheet from "./ui/PlansSheet";
import ShareSheet from "./ui/ShareSheet";
import ItinerarySheet from "./ui/ItinerarySheet";
import {
  addPoint,
  addWaypoint,
  applyLegSolution,
  deletePoint,
  exit,
  insertPoint,
  loadPlan,
  movePoint,
  nearestOnLeg,
  outAndBack,
  previewPoint,
  redo,
  removeWaypoint,
  reverse,
  select,
  start,
  toggleDayBreak,
  undo,
  type PlannerState,
} from "./plan/plannerCore";
import {
  ensureRouteOverlay,
  flashSnap,
  ROUTE_POINTS_LAYER,
  ROUTE_WAYPOINTS_LAYER,
  setPlanGhost,
  setScrubPoint,
  type RouteOverlayLeg,
  type RouteOverlayView,
} from "./plan/RouteOverlay";
import { loadRouteLook, saveRouteLook, type RouteLook } from "./plan/routeLook";
import { loadMapMarkings, saveMapMarkings, type MapMarkings } from "./map/mapMarkings";
import { etaSeconds, gainLossM, lineDistanceM, maxGradePct } from "./plan/geo";
import { terrainSamplerFor } from "./plan/elevation";
import { controlPointDistM, dayStats, type DayStat } from "./plan/daystats";
import { fuelGaps } from "./plan/fuel";
import { gradeRuns } from "./plan/grade";
import { planRouterFor, solveLeg, type PlanRouter } from "./plan/router";
import { closureWarnings, seasonsFor, type ClosureWarning, type SeasonEntry } from "./plan/seasons";
import { decodeShareFragment, SHARE_PATH } from "./plan/share";
import { savePlan, saveDraft, loadDraft } from "./plan/store";
import {
  emptyPlan,
  planGeometry,
  type ElevationSample,
  type LonLat,
  type Plan,
  type PlanStats,
  type SavedPlan,
} from "./plan/types";

// maplibre-gl + pmtiles are the bulk of the bundle — split into their own
// chunk instead of inflating the entry bundle. React.lazy's factory only
// runs the first time Suspense actually reaches <MapCanvas>, which would
// otherwise wait behind the states.json/manifest.json fetches in load()
// below (phase only reaches "ready" once those resolve) — a waterfall, not
// the parallel load the chunk split is for. Firing the same import() here,
// at module load, starts the fetch immediately; the module loader
// dedupes it, so React.lazy's later call just reuses this one in flight.
const mapChunk = () => import("./map/MapCanvas");
void mapChunk();
const MapCanvas = lazy(mapChunk);

const SUN_REFRESH_MS = 10 * 60 * 1000;
/** How long after the last plan edit to wait before re-sampling terrain /
 * re-writing the draft — long enough that a drag's rapid previewPoint
 * calls (one per mousemove) don't hammer the terrain sampler or
 * localStorage, short enough that stats/autosave still feel live. */
const PLAN_SETTLE_MS = 350;
/** Hold duration before the look control opens its picker instead of
 * cycling — long enough that an ordinary tap never mistakes itself for a
 * long-press. */
const LOOK_LONG_PRESS_MS = 450;
/** The waypoint menu's kind vocabulary — verbatim from the phone's
 * confirmationDialog in PlanScreen.swift (ForEach over this same seven-
 * word list). Order matters: it's what the menu renders top to bottom. */
const WAYPOINT_KINDS = [
  "Gate",
  "Water crossing",
  "Obstacle",
  "Campsite",
  "Fuel",
  "Photo spot",
  "Turnoff",
] as const;
/** Ceiling on simultaneous solveLeg calls — otherwise flipping SNAP on
 * after importing a long GPX track (hundreds of straight legs, all
 * eligible at once) fires that many concurrent A* solves in one breath.
 * The leg-solver effect below enforces this by only marking a leg
 * "attempted" when it's actually dispatched; anything past the cap is left
 * unattempted and gets picked up on a later pass once a slot frees up. */
const CONCURRENT_SOLVE_CAP = 4;
/** Fixed estimate of the waypoint menu's rendered footprint (title + 7
 * kind buttons + CANCEL, each ~40px including .anchor-menu's own gap/
 * padding) for the edge-clamp below — measuring the real box would need a
 * layout effect and a visible flash before the first clamp; a fixed
 * generous size is simpler and only ever errs toward extra margin, never a
 * clipped menu. */
const WAYPOINT_MENU_W = 200;
const WAYPOINT_MENU_H = 380;
/* .anchor-menu-btn grows from 38px to 44px under pointer:coarse (theme.css,
 * mobile pass) — eight rows (7 kinds + CANCEL) at the taller height need
 * more clamping room than the mouse/right-click estimate above budgets
 * for. Same "fixed, generous, simpler than measuring" philosophy as
 * WAYPOINT_MENU_H itself. */
const WAYPOINT_MENU_H_COARSE = 440;
const WAYPOINT_MENU_MARGIN = 8;

/** Hit-test padding around a tap/click when probing for a control point or
 * waypoint under the cursor (map click/contextmenu/long-press handlers
 * below) — wider on touch than the mouse-era ±6px a fingertip is far less
 * precise than a cursor. 12px matches the insert-on-line ghost's own
 * "how close counts as a hit" tolerance elsewhere in this file (the
 * mousemove handler below). Checked live rather than cached: a hybrid
 * device's active pointer type can change between one event and the next
 * (e.g. a touchscreen laptop used with a mouse). */
function hitPad(): number {
  return matchMedia("(pointer: coarse)").matches ? 12 : 6;
}

type Phase =
  | { kind: "loading" }
  | { kind: "error"; message: string; retry: () => void }
  | { kind: "ready"; states: StateEntry[]; state: StateEntry; manifest: StateManifest };

/** Terrain-sampling lifecycle for the active draw session — PlanBar and
 * ProfileStrip both key their rendering off it so "still working," "tried
 * and found nothing," and "nothing to try yet" each get their own words
 * instead of all rendering as the same silence (see
 * docs/WEB_PLANNER_UX_REVIEW.md P1 #4).
 *
 * - idle: under 2 geometry points — nothing worth sampling.
 * - pending: a debounce or an in-flight sampleLine() call.
 * - ready: sampleLine() resolved with ≥2 samples.
 * - unavailable: no terrain layer for this state, sampleLine() rejected,
 *   or it resolved with < 2 samples on a ≥2-point route.
 */
export type SamplingState = "idle" | "pending" | "ready" | "unavailable";

const LOOKS = new Set<string>([
  "outdoors",
  "dustlarkTopo",
  "ustopo",
  "hillshade",
  "historic",
  "imagery",
]);

/** The planner starts in browse — no route being drawn. The placeholder
 * plan's content is never read until start()/loadPlan() replaces it. */
function browsePlanner(): PlannerState {
  return { mode: "browse", plan: emptyPlan("", ""), editingId: null, selected: null, undo: [], redo: [] };
}

/** 5-decimal-rounded endpoint key for the leg-solver's attempted set — same
 * tolerance as plannerCore's applyLegSolution guard (see its doc comment),
 * so "this pair was tried" and "the endpoints still match" agree. */
function legKey(a: LonLat, b: LonLat): string {
  return `${a[0].toFixed(5)},${a[1].toFixed(5)}|${b[0].toFixed(5)},${b[1].toFixed(5)}`;
}

/** Clamps a cursor-anchored popup's top-left so its full w×h box stays
 * within the viewport, minus `margin` on the trailing edges — shared by
 * the waypoint menu's desktop right-click and touch long-press open
 * paths, both of which just hand raw clientX/clientY to the same state.
 * Only the right/bottom edges need guarding (x/y are always ≥0 — they
 * come from a click/touch inside the map canvas), but the lower Math.max
 * bound is cheap insurance against a viewport narrower/shorter than the
 * menu itself, where the naive Math.min alone would push it negative. */
function clampMenuPos(x: number, y: number, w: number, h: number, margin: number): { x: number; y: number } {
  return {
    x: Math.max(margin, Math.min(x, window.innerWidth - w - margin)),
    y: Math.max(margin, Math.min(y, window.innerHeight - h - margin)),
  };
}

/** The plan-wide name suggestion for PlanBar's DONE naming prefill: the
 * most FREQUENT way name across snapped legs (router.ts's per-leg
 * dominant-name field), ties broken by whichever name's legs cover more
 * total distance. "Leg granularity" ties, per the ask: summed from each
 * LEG's own flattened length (points[i]..via..points[i+1]), not re-derived
 * at the way level — solveLeg's SolvedLeg already discarded that finer
 * breakdown (see router.ts's own comment on why), so this is the finest
 * granularity available up here. null when no snapped leg carries a name
 * at all (nothing solved yet, SNAP is off, or every crossed way is
 * unnamed in the source data). */
function suggestedRouteName(plan: Plan): string | null {
  const byName = new Map<string, { count: number; metersM: number }>();
  for (let i = 0; i < plan.legs.length; i++) {
    const leg = plan.legs[i];
    if (leg.mode !== "snapped" || !leg.name) continue;
    const metersM = lineDistanceM([plan.points[i], ...leg.via, plan.points[i + 1]]);
    const entry = byName.get(leg.name) ?? { count: 0, metersM: 0 };
    entry.count++;
    entry.metersM += metersM;
    byName.set(leg.name, entry);
  }
  let best: string | null = null;
  let bestCount = 0;
  let bestMeters = 0;
  for (const [name, { count, metersM }] of byName) {
    if (count > bestCount || (count === bestCount && metersM > bestMeters)) {
      best = name;
      bestCount = count;
      bestMeters = metersM;
    }
  }
  return best;
}

/** Frames the map over a point set's bounding box — shared by opening a
 * saved plan and landing a freshly-imported one, both of which need to
 * show the whole route rather than wherever the camera last was. */
function fitBoundsToPoints(map: maplibregl.Map, points: LonLat[]): void {
  if (points.length === 0) return;
  let [minLon, minLat] = points[0];
  let [maxLon, maxLat] = points[0];
  for (const [lon, lat] of points) {
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  map.fitBounds(
    [
      [minLon, minLat],
      [maxLon, maxLat],
    ],
    { padding: 80 },
  );
}

export default function App() {
  const initial = useMemo(() => parseHash(location.hash), []);
  // The share open-path (docs/SHARE_LINK_SPEC.md) reads the RAW hash a
  // share link arrives with, which parseHash's k=v reading above can't
  // make sense of (no '&'/'=' survive base64url, so it just yields {} —
  // harmless, but not useful). Captured once, synchronously, before the
  // phase-ready effect further down can overwrite location.hash with its
  // own #state=…&look=…&at= shape; consumed and nulled by the
  // "share open-path" effect below, so it only ever fires once.
  const pendingShareRef = useRef<{ hash: string; isSharePath: boolean } | null>({
    hash: location.hash,
    isSharePath: location.pathname === SHARE_PATH,
  });
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [look, setLook] = useState<MapLook>(
    LOOKS.has(initial.look) ? (initial.look as MapLook) : "outdoors",
  );
  // Route line Colour/Weight (Sep 14 2026 — plan/routeLook.ts): per-device
  // via localStorage, deliberately NOT part of the URL hash `look` reads
  // from above — it's never per-trip and never in a share link (decision
  // (1) of the RouteLook contract).
  const [routeLook, setRouteLookState] = useState<RouteLook>(() => loadRouteLook());
  const setRouteLook = (next: RouteLook) => {
    setRouteLookState(next);
    saveRouteLook(next);
  };
  // Map markings (Sep 16 2026 — map/mapMarkings.ts): per-device via
  // localStorage on exactly the same terms as routeLook above — never
  // per-trip, never in a share link. Unlike routeLook, a change here
  // rebuilds the map style (MapCanvas's own effect): the sizes and widths
  // are baked into zoom interpolations at generation time, not paint
  // properties a live setter could move.
  const [markings, setMarkingsState] = useState<MapMarkings>(() => loadMapMarkings());
  const setMarkings = (next: MapMarkings) => {
    setMarkingsState(next);
    saveMapMarkings(next);
  };
  const night = useNight();
  const sun = useLiveSun(phase.kind === "ready" ? phase.state : null);
  // Layer toggles — Symbology v2's 43 rows, still stored as one JSON object
  // under "layers" and still merged OVER the table's defaults, which is what
  // makes the row count growable: a browser that saved v1's ten keys comes
  // back with those ten and picks up the table's own default for the other
  // 33, rather than finding 33 undefined switches.
  const [toggles, setToggles] = useState<LayerToggles>(() => {
    try {
      const raw = localStorage.getItem("layers");
      const saved = raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
      const merged: LayerToggles = { ...DEFAULT_TOGGLES, ...saved };
      // The UTM grid was its own boolean under its own key before it became
      // a row (it is a runtime overlay, so it had no baked layer to flip and
      // sat outside LayerToggles entirely). Carry that choice across once:
      // only while the saved object has no opinion of its own yet.
      if (!Object.prototype.hasOwnProperty.call(saved, "showUTMGrid")) {
        merged.showUTMGrid = localStorage.getItem("dustlark.utmGrid.v1") === "1";
      }
      return merged;
    } catch {
      return DEFAULT_TOGGLES;
    }
  });
  const [camera, setCamera] = useState<CameraState | undefined>(() => {
    const [lon, lat, zoom] = (initial.at ?? "").split(",").map(Number);
    return Number.isFinite(lon) && Number.isFinite(lat) && Number.isFinite(zoom)
      ? { lon, lat, zoom }
      : undefined;
  });
  const [showLayers, setShowLayers] = useState(false);
  const [showStates, setShowStates] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  // ITINERARY's own overlay — reached from PlanBar (only visible while
  // drawing, same as the bar itself), not from the top HUD rail, so it
  // joins the `share` pattern below rather than openPanel's mutually-
  // exclusive set: exiting draw mode already tears down PlanBar and
  // everything under it, so there's no "leave this open while switching to
  // LAYERS" case to guard against the way openPanel's set does.
  const [showItinerary, setShowItinerary] = useState(false);
  // ShareSheet's own overlay — not one of openPanel's mutually-exclusive
  // set (it's reached FROM inside PlansSheet, not the HUD directly), so it
  // gets a plain nullable-payload state like waypointMenu/toast below
  // rather than joining that union. handleShare (past the early returns)
  // closes showPlans in the same call that opens this, keeping the
  // one-overlay-at-a-time rule without widening openPanel for a sheet it
  // never toggles directly.
  const [share, setShare] = useState<{ plan: Plan; url: string } | null>(null);

  // The look control's long-press/right-click picker — joins the
  // one-overlay-at-a-time family only loosely (below): it's a mini-popover,
  // not a sheet, so opening it doesn't close anything else, but any sheet
  // opening over it does close it. lookPressTimerRef is the pointerdown ->
  // 450ms-hold timer; suppressLookClickRef stops the click that follows the
  // long-press's own pointerup from ALSO cycling the look — armed when the
  // timer fires, disarmed by whichever comes first: that click consuming
  // it, or the menu closing some other way (closeLookMenu, below).
  const [showLookMenu, setShowLookMenu] = useState(false);
  const lookPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressLookClickRef = useRef(false);
  const clearLookPressTimer = () => {
    if (lookPressTimerRef.current !== null) {
      clearTimeout(lookPressTimerRef.current);
      lookPressTimerRef.current = null;
    }
  };
  const closeLookMenu = () => {
    setShowLookMenu(false);
    suppressLookClickRef.current = false;
  };

  /** One overlay at a time — Search opened over the Routes sheet used to
   * stack both panels into a visibly broken double-layer state. */
  const openPanel = (which: "layers" | "states" | "search" | "plans" | null) => {
    setShowLayers(which === "layers");
    setShowStates(which === "states");
    setShowSearch(which === "search");
    setShowPlans(which === "plans");
    closeLookMenu();
  };
  const openPanelRef = useRef(openPanel);
  openPanelRef.current = openPanel;
  const showSearchRef = useRef(showSearch);
  showSearchRef.current = showSearch;

  // ------------------------------------------------------------- planner
  //
  // New-route drawing: plannerCore.ts is the pure reducer, this component
  // just dispatches into it off user input (map clicks/drags, keyboard,
  // PlanBar/PlansSheet callbacks) and owns everything reducer.ts
  // deliberately doesn't — the map instance, elevation sampling, and
  // persistence (draft autosave / save / load).
  const [planner, setPlanner] = useState<PlannerState>(browsePlanner);
  const plannerRef = useRef(planner);
  plannerRef.current = planner;

  // Snap-to-trail: global toggle (iOS parity with the phone's
  // straightMode), default on — inert wherever `router` below is null, so
  // there's no need to reset it on a state switch. Applies to NEW
  // placements only; see the click/drag handlers in handleMapReady.
  const [snapMode, setSnapMode] = useState(true);
  const snapModeRef = useRef(snapMode);
  snapModeRef.current = snapMode;
  // In-flight leg-solve count, for PlanBar's SOLVING… chip — also read (via
  // the ref below) by the leg-solver effect itself to enforce
  // CONCURRENT_SOLVE_CAP, since the effect's own dependency array can't
  // include `solving` without retriggering itself on every solve's own
  // increment/decrement.
  const [solving, setSolving] = useState(0);
  const solvingRef = useRef(solving);
  solvingRef.current = solving;
  // "Searches actually still running," for PlanBar's dim SOLVING LONG
  // ROUTE… hint — deliberately its OWN counter rather than reading
  // solveControllersRef.current.size directly: that map's size can't
  // drive a render on its own (mutating a ref never does), and mirroring
  // it into state on every set/delete would be the same amount of code
  // for no benefit over a plain incremented-on-dispatch/decremented-in-
  // onSettled counter. Long-lived on purpose — unlike `solving` above
  // (which only covers the first 12s fast window and always drains back
  // to 0 by then), this stays >0 for as long as solveLeg's own background
  // continuation is genuinely still searching, which can be up to 120s.
  const [longSolving, setLongSolving] = useState(0);
  // Endpoint-pair keys (legKey) already handed to solveLeg this draw
  // session — see the leg-solver effect below. Cleared whenever draw mode
  // isn't active, so a fresh session starts clean.
  const attemptedRef = useRef<Set<string>>(new Set());
  // In-flight solveLeg abort handles, keyed by the same legKey as
  // attemptedRef — lets the leg-solver effect below cancel a solve whose
  // endpoints no longer exist in the plan, and lets a background upgrade
  // (past the 12s fast path) find its own entry again once it lands.
  const solveControllersRef = useRef<Map<string, AbortController>>(new Map());
  // The insert-on-line hover ghost's current hit (or none) — set by the
  // mousemove handler in handleMapReady, read by the click handler to
  // decide insert-vs-append, per the click handler's own comment.
  const ghostRef = useRef<{ legIndex: number; at: LonLat } | null>(null);

  const [samples, setSamples] = useState<ElevationSample[]>([]);
  const [sampling, setSampling] = useState<SamplingState>("idle");
  const [profileOpen, setProfileOpen] = useState(() => {
    try {
      return localStorage.getItem("dustlark.profileOpen") === "true";
    } catch {
      return false;
    }
  });
  // Grade-colored route line — persisted the same degrade-on-failure way
  // as profileOpen above (store.ts's philosophy: a setting that can't
  // survive private mode just doesn't, rather than throwing).
  const [gradeOn, setGradeOn] = useState(() => {
    try {
      return localStorage.getItem("dustlark.gradeline.v1") === "1";
    } catch {
      return false;
    }
  });
  // UTM grid — a Symbology v2 row (`showUTMGrid`) since Sep 22 2026, so it
  // lives in `toggles` with the other 42 and the presets can write it. It
  // is still generated at runtime from the camera (map/utm-grid.ts) rather
  // than baked into the style, so it is NOT in TOGGLE_GROUPS and still
  // reaches the map as its own MapCanvas prop; only the storage moved.
  const showUTMGrid = toggles.showUTMGrid;
  // Seasonal-closure entries for the active region — null until the lazy-
  // load effect below fetches them (or finds this state serves none).
  // Reset to null on a region switch by the same effect, so a NEW region's
  // ways are never checked against the OLD region's closures.
  const [seasonEntries, setSeasonEntries] = useState<SeasonEntry[] | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  // Read by the map click/drag handlers below (registered once, off refs)
  // so a click that misses the small "Discard route?" bar and lands on the
  // map behind it doesn't sneak in a stray point while that decision is
  // still pending.
  const confirmDiscardRef = useRef(confirmDiscard);
  confirmDiscardRef.current = confirmDiscard;
  // Same hazard, other sub-state: PlanBar's DONE name-input is local to the
  // bar, so it mirrors open/closed out through this ref for the same
  // stray-click gate.
  const namingRef = useRef(false);
  // The right-click "add a waypoint here" menu — open over the map at a
  // stored lngLat/screen position, or null. Same stray-click hazard as
  // confirmDiscard/naming: mirrored into a ref so the map's click/drag/
  // mousemove handlers (registered once in handleMapReady, off refs) can
  // suppress themselves while it's open without needing to be re-bound.
  const [waypointMenu, setWaypointMenu] = useState<{ at: LonLat; x: number; y: number } | null>(
    null,
  );
  const waypointMenuRef = useRef(waypointMenu);
  waypointMenuRef.current = waypointMenu;
  // Focuses the menu's first kind button whenever it opens — the same
  // "focus routes Escape to this dialog's own handler" reasoning as
  // PlanBar's overflow/closures menus and LookMenu (see their own
  // comments), extended here since this menu previously opened (via
  // right-click or long-press, both mouse/touch gestures) with no focus
  // target inside it at all.
  const waypointMenuFirstBtnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (waypointMenu) waypointMenuFirstBtnRef.current?.focus();
  }, [waypointMenu]);
  // Bumped by the map's dblclick-to-finish handler and by Enter (both in
  // handleMapReady/the keydown effect below) — PlanBar opens its naming
  // form on every change. A nonce rather than a boolean so it can fire
  // more than once per draw session without needing a "consumed" reset.
  const [doneNonce, setDoneNonce] = useState(0);

  // Transient "SAVED" pill — DONE otherwise drops straight back to browse
  // with zero acknowledgment (docs/WEB_PLANNER_UX_REVIEW.md P1 #3).
  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = (text: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(text);
    toastTimerRef.current = setTimeout(() => setToast(null), 1400);
  };

  // One polite screen-reader announcement, fired from committed edits only
  // (add/delete/undo/redo, never a drag preview — see the effect below)
  // plus the save flow.
  const [announce, setAnnounce] = useState("");
  // Baseline point count for the CURRENT draw session — null outside draw
  // mode, so the first tick after entering draw never announces the
  // starting count as though it were a change.
  const announcedCountRef = useRef<number | null>(null);

  // Badges the PLAN control when a resumable draft exists — checked on
  // mount and on every return to browse (see the effect below).
  const [hasDraft, setHasDraft] = useState(false);

  const mapRef = useRef<maplibregl.Map | null>(null);
  // Latest render's overlay view, read by the "styledata" listener
  // (registered once in handleMapReady) — same ref-mirroring trick
  // MapCanvas uses for togglesRef, since a style swap can land between
  // renders and must re-paint with whatever the CURRENT plan/selection is,
  // not whatever it was when the listener was registered.
  const routeViewRef = useRef<RouteOverlayView>({
    legs: [],
    points: [],
    waypoints: [],
    selected: null,
    active: false,
  });
  // Same ref-mirroring trick as routeViewRef immediately above, same
  // reason: handleMapReady's "styledata" listener is registered once
  // (empty-deps useCallback) and must read whatever Colour/Weight is
  // CURRENT at paint time, not whatever it was when the listener was
  // wired up.
  const routeLookRef = useRef<RouteLook>(routeLook);
  // Same trick again for the markings theme — the overlay's own labels and
  // marker art scale with it (RouteOverlay's applyOverlayMarkings).
  const markingsRef = useRef<MapMarkings>(markings);
  // The drag contract: dragOriginRef holds the STABLE pre-drag state (see
  // previewPoint's doc comment in plannerCore.ts) so every preview frame —
  // and the final commit — reads off the same snapshot instead of chaining.
  const dragOriginRef = useRef<PlannerState | null>(null);
  const dragIndexRef = useRef<number | null>(null);
  // Set right after the click handler ADDS a point (not selects); read by
  // that same handler's detail>=2 branch to undo it again if this turns
  // out to have been the first half of a double-click-to-finish. See the
  // click handler's own comment in handleMapReady for why.
  const pendingClickAddRef = useRef<{ at: number } | null>(null);
  // Set when a touch long-press just opened the waypoint menu — the finger
  // lift that follows fires a synthetic click, and without this flag that
  // click's own "map click while menu open dismisses it" rule would close
  // the menu in the same gesture that opened it. Consumed (reset) by the
  // very next click. Same one-shot-suppression shape as the look button's
  // long-press handling.
  const suppressTouchClickRef = useRef(false);
  const sampleTokenRef = useRef(0);
  // handleMapReady's retry-armed re-ensure, shared with the planner-change
  // effect below so every paint path self-heals the same way.
  const reEnsureRef = useRef<(() => void) | null>(null);

  // Every seasons.json id this plan's snapped legs reference — the signal
  // for whether it's worth fetching seasons.json at all (see the lazy-load
  // effect above): most routes never cross a closed way, so most draw
  // sessions never need that fetch.
  const legClosureIds = useMemo(() => {
    const ids = new Set<number>();
    for (const leg of planner.plan.legs) {
      for (const id of leg.closureIds ?? []) ids.add(id);
    }
    return [...ids].sort((a, b) => a - b);
  }, [planner.plan]);

  const closures = useMemo(
    () =>
      seasonEntries && legClosureIds.length > 0
        ? closureWarnings(planner.plan, seasonEntries, new Date())
        : [],
    [planner.plan, seasonEntries, legClosureIds],
  );

  // Grade-classified runs for the map's grade-colored line — only computed
  // while the toggle is on, the same "don't do the work if it can't be
  // seen" restraint as the terrain sampler skipping states with no terrain
  // layer.
  const gradeLineRuns = useMemo(() => (gradeOn ? gradeRuns(samples) : []), [gradeOn, samples]);

  routeLookRef.current = routeLook;
  markingsRef.current = markings;
  routeViewRef.current = {
    legs: planner.plan.legs.map(
      (leg, i): RouteOverlayLeg => ({
        mode: leg.mode,
        coords: [planner.plan.points[i], ...leg.via, planner.plan.points[i + 1]],
      }),
    ),
    points: planner.plan.points,
    waypoints: planner.plan.waypoints,
    selected: planner.selected,
    active: planner.mode === "draw",
    dayBreaks: (planner.plan.dayBreaks ?? [])
      .filter((i) => i > 0 && i < planner.plan.points.length - 1)
      .map((pi, k) => ({ at: planner.plan.points[pi], label: "N" + (k + 1) })),
    closures: closures.map((w) => w.coords),
    gradeOn,
    gradeRuns: gradeLineRuns,
  };

  // Distance is synchronous (pure geometry); gain/loss/grade/ETA need
  // terrain samples and stay null until sampleLine() resolves below.
  const stats: PlanStats = useMemo(() => {
    const distanceM = lineDistanceM(planGeometry(planner.plan));
    if (samples.length < 2) {
      return { distanceM, gainM: null, lossM: null, maxGradePct: null, etaSeconds: null };
    }
    const { gainM, lossM } = gainLossM(samples);
    return { distanceM, gainM, lossM, maxGradePct: maxGradePct(samples), etaSeconds: etaSeconds(samples) };
  }, [planner.plan, samples]);

  // Per-day breakdown for PlanBar's second stats row — dayStats' own doc:
  // one entry per dayBreaks segment, distance always real, the elevation
  // fields null until enough samples fall inside that day's span.
  const days: DayStat[] = useMemo(() => dayStats(planner.plan, samples), [planner.plan, samples]);

  // PlanBar's DONE-naming prefill for a plan that's never been named —
  // see suggestedRouteName's own doc above for how "the" name is picked.
  const suggestedName = useMemo(() => suggestedRouteName(planner.plan), [planner.plan]);

  // Longest gap between this plan's Fuel waypoints (plan/fuel.ts) —
  // PlanBar's FUEL GAP chip. Only the max is surfaced today; fuelGaps'
  // full per-gap breakdown is there for a future per-stop view.
  const fuelGapM = useMemo(() => fuelGaps(planner.plan)?.maxGapM ?? null, [planner.plan]);

  // Overnight markers' distance-along-route, for ProfileStrip's vertical
  // reference lines — filtered/labeled the same way routeViewRef's own
  // dayBreaks is above, so a marker on the map and its line on the profile
  // agree on which "N1"/"N2" is which.
  const dayMarks = useMemo(() => {
    const cum = controlPointDistM(planner.plan);
    return (planner.plan.dayBreaks ?? [])
      .filter((i) => i > 0 && i < planner.plan.points.length - 1)
      .map((pi, k) => ({ distM: cum[pi], label: "N" + (k + 1) }));
  }, [planner.plan]);

  // One sampler per manifest (its terrain pmtiles URL), reused across the
  // whole draw session — reads `phase` directly (not the later-destructured
  // `manifest`) so this hook can sit unconditionally before the early
  // returns below, same reasoning as useLiveSun.
  const sampler = useMemo(() => {
    if (phase.kind !== "ready") return null;
    const url = layerURL(phase.manifest, "terrain");
    return url ? terrainSamplerFor(url) : null;
  }, [phase]);

  // Snap-to-trail router for this state — null when it serves no DLR2 v2
  // graph or no snap grid (planRouterFor's own contract). Same lazy-open-
  // on-first-use pattern as the terrain sampler above: this useMemo just
  // builds the facade, the actual routing.bin/routing.snap.bin fetches
  // happen on first snap()/solve() call. Mirrored into a ref for the map's
  // click/drag handlers below (registered once in handleMapReady, so they
  // must never close over a stale router).
  const router = useMemo<PlanRouter | null>(() => {
    if (phase.kind !== "ready") return null;
    return planRouterFor(phase.manifest);
  }, [phase]);
  const routerRef = useRef(router);
  routerRef.current = router;

  // Debounced terrain sampling, with a stale-response guard: a slow sample
  // for an OLD geometry must never clobber a newer one that resolved first.
  // Also drives `sampling` (see its doc comment above) — note `samples`
  // itself is deliberately NOT cleared on the "pending" transition, only on
  // idle/unavailable: a re-solve triggered by a drag should keep showing
  // the last-good numbers (dimmed by PlanBar/ProfileStrip) rather than
  // blanking every stat on every mousemove.
  useEffect(() => {
    if (planner.mode !== "draw") {
      setSamples([]);
      setSampling("idle");
      return;
    }
    const geometry = planGeometry(planner.plan);
    if (geometry.length < 2) {
      setSamples([]);
      setSampling("idle");
      return;
    }
    if (!sampler) {
      setSamples([]);
      setSampling("unavailable");
      return;
    }
    const token = ++sampleTokenRef.current;
    setSampling("pending");
    const id = setTimeout(() => {
      sampler
        .sampleLine(geometry)
        .then((result) => {
          if (sampleTokenRef.current !== token) return;
          setSamples(result);
          setSampling(result.length >= 2 ? "ready" : "unavailable");
        })
        .catch((e) => {
          // Sampler failure degrades to distance-only stats, never a crash
          // — log once so a broken terrain layer doesn't spam the console.
          if (sampleTokenRef.current === token) {
            setSamples([]);
            setSampling("unavailable");
            console.warn("terrain sampling failed", e);
          }
        });
    }, PLAN_SETTLE_MS);
    return () => clearTimeout(id);
  }, [planner.mode, planner.plan, sampler]);

  // Declarative leg solver — the heart of snap-to-trail. Watches the
  // SETTLED plan (same PLAN_SETTLE_MS debounce as terrain sampling above,
  // and for the identical reason: previewPoint fires once per drag
  // mousemove, and firing an A* solve on every one of those would be both
  // wasteful and pointless — only the position the user actually lets go
  // of is worth solving). On settle, every leg that's straight, has an
  // empty via (never solved), and whose endpoint pair hasn't been tried
  // yet this draw session gets one solveLeg call; applyLegSolution's own
  // staleness guard means a result that arrives after the geometry moved
  // on is just dropped, not misapplied — this effect doesn't need to know
  // that happened, the next settle (with a new key) tries again.
  useEffect(() => {
    if (planner.mode !== "draw" || !snapMode || !router) return;
    const id = setTimeout(() => {
      // A leg whose key no longer matches any CURRENT leg (its endpoints
      // moved, or it was deleted/undone) may still have a solve running
      // under it — abort that controller and un-mark its key as attempted,
      // so if the same endpoint pair reappears later (a drag back to where
      // it started) it gets a fresh solve instead of being skipped as
      // already-tried.
      const { points, legs } = planner.plan;
      const currentKeys = new Set(legs.map((_, i) => legKey(points[i], points[i + 1])));
      for (const [key, ctrl] of solveControllersRef.current) {
        if (!currentKeys.has(key)) {
          ctrl.abort();
          solveControllersRef.current.delete(key);
          attemptedRef.current.delete(key);
        }
      }

      // Undo/redo can restore a leg to the exact straight/empty geometry
      // it had BEFORE a solve, on a key that's already in attemptedRef
      // from earlier this session — that search already settled (fast
      // straight, a late null, or was pruned above) with no live
      // controller left for it, and nothing else ever re-marks the key,
      // so without this the leg is stuck dashed for the rest of the
      // session. A completed-or-aborted attempt on a leg that's STILL
      // unsolved deserves a fresh try; a key with a LIVE controller (still
      // in the map) keeps its mark, since that search may yet resolve it
      // on its own.
      for (let i = 0; i < legs.length; i++) {
        if (legs[i].mode !== "straight" || legs[i].via.length > 0) continue;
        const key = legKey(points[i], points[i + 1]);
        if (attemptedRef.current.has(key) && !solveControllersRef.current.has(key)) {
          attemptedRef.current.delete(key);
        }
      }

      // Cap how many NEW solves this pass may dispatch, on top of whatever
      // is already in flight — never mark a leg attempted past that
      // budget. applyLegSolution's setPlanner call (below) changes
      // `planner.plan` on a normal resolution, which re-runs this very
      // effect; that re-run sees a freed slot and picks up the legs this
      // pass skipped (they were never added to attemptedRef), draining the
      // queue a few at a time instead of firing everything at once.
      const capacity = CONCURRENT_SOLVE_CAP - solvingRef.current;
      if (capacity <= 0) return;
      const targets: { legIndex: number; from: LonLat; to: LonLat }[] = [];
      for (let i = 0; i < legs.length && targets.length < capacity; i++) {
        if (legs[i].mode !== "straight" || legs[i].via.length > 0) continue;
        const from = points[i];
        const to = points[i + 1];
        const key = legKey(from, to);
        if (attemptedRef.current.has(key)) continue;
        attemptedRef.current.add(key);
        targets.push({ legIndex: i, from, to });
      }
      if (targets.length === 0) return;
      setSolving((n) => n + targets.length);
      for (const t of targets) {
        const key = legKey(t.from, t.to);
        const ctrl = new AbortController();
        solveControllersRef.current.set(key, ctrl);
        setLongSolving((n) => n + 1);
        void solveLeg(router, t.from, t.to, {
          signal: ctrl.signal,
          onUpgrade: (leg) => {
            // A late upgrade for a leg the user has since edited away is
            // safe to apply blindly — applyLegSolution's own geometry
            // guard drops it if the endpoints it was solved for no longer
            // match whatever now sits at legIndex. The controller entry
            // itself is retired by onSettled below, not here — see its
            // comment for why that's now the ONE place this happens.
            setPlanner((s) => applyLegSolution(s, t.legIndex, t.from, t.to, leg));
            // Same magnetize acknowledgment as a live snap — the dashed→
            // solid flip deserves the same nicety even though this one
            // lands seconds later, in the background.
            if (mapRef.current) flashSnap(mapRef.current, t.to);
          },
          onSettled: () => {
            // Fires exactly once, whenever THIS key's underlying search
            // finishes — fast-snapped, fast-straight, a late upgrade, a
            // late null, or an error (solveLeg's own guarantee, router.ts).
            // Replaces the old partial deletes that used to live here and
            // in onUpgrade above: those left a leg that resolved
            // straight-FAST sitting in the controllers map forever, since
            // solveLeg previously exposed no "the search itself is truly
            // done" signal for that case — onSettled is that signal.
            solveControllersRef.current.delete(key);
            setLongSolving((n) => n - 1);
          },
        }).then((leg) => {
          setPlanner((s) => applyLegSolution(s, t.legIndex, t.from, t.to, leg));
          setSolving((n) => n - 1);
        });
      }
    }, PLAN_SETTLE_MS);
    return () => clearTimeout(id);
  }, [planner.mode, planner.plan, snapMode, router]);

  // A fresh draw session starts with a clean attempted-set — otherwise a
  // coordinate pair reused across two different sessions (astronomically
  // unlikely for real trail clicks, but free to guard) could wrongly skip
  // a solve, and the set would just grow forever across a long browsing
  // session. Leaving draw mode also means no in-flight (or background-
  // continuation) solve is worth keeping alive — abort every controller
  // rather than let them run to a result nothing will ever apply.
  useEffect(() => {
    if (planner.mode !== "draw") {
      attemptedRef.current.clear();
      for (const ctrl of solveControllersRef.current.values()) ctrl.abort();
      solveControllersRef.current.clear();
    }
  }, [planner.mode]);

  // Leaving draw mode (DONE, discard, Escape) must drop any lingering
  // insert-on-line ghost — otherwise the next draw session could briefly
  // inherit a stale hover marker from wherever the cursor last was.
  useEffect(() => {
    if (planner.mode !== "draw") {
      ghostRef.current = null;
      if (mapRef.current) setPlanGhost(mapRef.current, null);
    }
  }, [planner.mode]);

  // Same reasoning, for the waypoint-add menu — a fresh draw session must
  // never inherit a menu left open from the last one.
  useEffect(() => {
    if (planner.mode !== "draw") setWaypointMenu(null);
  }, [planner.mode]);

  // Committed-change announcement: points.length only ever changes via
  // addPoint/deletePoint/undo/redo (never previewPoint/movePoint, which
  // move an existing point without adding or removing one) — so diffing it
  // against the session's baseline naturally excludes drag previews without
  // needing to know WHICH action fired.
  useEffect(() => {
    if (planner.mode !== "draw") {
      announcedCountRef.current = null;
      return;
    }
    const n = planner.plan.points.length;
    if (announcedCountRef.current !== null && announcedCountRef.current !== n) {
      setAnnounce(`${n} points`);
    }
    announcedCountRef.current = n;
  }, [planner.mode, planner.plan.points.length]);

  // The PLAN control's resumable-draft badge — re-checked every time the
  // planner lands back in browse (a save, a discard, or exiting an
  // untouched resume all end here).
  useEffect(() => {
    if (planner.mode === "browse") {
      const d = loadDraft();
      setHasDraft(d !== null && d.points.length >= 2);
    }
  }, [planner.mode]);

  useEffect(() => {
    try {
      localStorage.setItem("dustlark.profileOpen", String(profileOpen));
    } catch {
      /* private mode — settings just don't persist */
    }
  }, [profileOpen]);

  useEffect(() => {
    try {
      localStorage.setItem("dustlark.gradeline.v1", gradeOn ? "1" : "0");
    } catch {
      /* private mode — settings just don't persist */
    }
  }, [gradeOn]);

  // A fresh region has its own seasons.json (or none) — never carry the
  // PRIOR region's parsed entries into a plan drawn somewhere else, which
  // would check ways that don't even exist in this graph. Keyed on the
  // whole `phase` the same way sampler/router above are: `load()` is the
  // only thing that ever changes it, so every change here IS a region
  // (re)establishing itself.
  useEffect(() => {
    setSeasonEntries(null);
  }, [phase]);

  // Seasonal-closure data is only worth fetching once this plan's snapped
  // legs actually cross a closed way — most routes never do, so this stays
  // unfetched until legClosureIds says otherwise. seasonEntries !== null
  // short-circuits every later settle once loaded (or once this state's
  // been confirmed to serve none); the reset effect above is what lets a
  // NEW region try again.
  useEffect(() => {
    if (
      planner.mode !== "draw" ||
      legClosureIds.length === 0 ||
      seasonEntries !== null ||
      phase.kind !== "ready"
    ) {
      return;
    }
    const load = seasonsFor(phase.manifest);
    if (!load) return; // this state serves no seasons layer
    void load().then(setSeasonEntries);
  }, [planner.mode, legClosureIds, seasonEntries, phase]);

  // Draw mode disables the map's native double-click-zoom: double-click is
  // repurposed to FINISH the route instead (see the click handler's
  // e.detail>=2 branch in handleMapReady) — zooming out from under an
  // in-progress drawing read as jarring on its own
  // (docs/WEB_PLANNER_UX_REVIEW.md P1 #6).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (planner.mode === "draw") map.doubleClickZoom.disable();
    else map.doubleClickZoom.enable();
  }, [planner.mode]);

  // Draft autosave — debounced for the same reason as sampling above (a
  // drag previews on every mousemove; only the settled position needs to
  // hit localStorage). Explicit saves/discards call saveDraft(null)
  // themselves and exit draw mode in the same tick, which cancels this
  // effect's pending timeout before it can re-write a stale draft.
  useEffect(() => {
    if (planner.mode !== "draw") return;
    const id = setTimeout(() => saveDraft(planner.plan), PLAN_SETTLE_MS);
    return () => clearTimeout(id);
  }, [planner.mode, planner.plan]);

  // The route overlay must survive map.setStyle() (App swaps styles on
  // every look/night/sun change, including the 10-minute live-sun tick) —
  // ensureRouteOverlay is idempotent, so re-running it here on every
  // planner change is exactly as safe as the "styledata" listener wired up
  // in handleMapReady below re-running it after a style swap. The extra
  // deps are the overlay inputs that live OUTSIDE planner state: the
  // grade toggle/runs (gradeOn is App state, and the runs also move when
  // terrain samples resolve mid-session) and the closure warnings (which
  // arrive off a seasons.json fetch) — without them, toggling GRADE or a
  // late fetch painted nothing until the next unrelated plan edit.
  useEffect(() => {
    reEnsureRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planner, gradeOn, gradeLineRuns, closures, routeLook, markings]);

  /** ✕ / Escape-with-nothing-selected. Confirm only when this session made
   * EDITS (undo history is the dirty flag) — reopening a saved route and
   * closing it untouched used to fire "Discard route?" over nothing, which
   * implies loss where there is none. With no edits: an untouched resumed
   * draft stays in the draft slot (exit must NOT clear it), an empty new
   * route leaves nothing behind. Stable (empty deps) so the keydown effect
   * below and PlanBar's ✕ button can both call it directly. */
  const requestClose = useCallback(() => {
    const s = plannerRef.current;
    if (s.undo.length > 0) {
      setConfirmDiscard(true);
    } else {
      if (s.plan.points.length === 0) saveDraft(null);
      setPlanner(exit(s));
    }
  }, []);

  /** ProfileStrip's hover-scrub, mirrored onto the map's plan-scrub layer —
   * imperative (setScrubPoint, not React state) so a 60 Hz pointermove
   * never has to round-trip through a re-render. Stable (empty deps) for
   * the same reason as requestClose above: a plain prop, no re-subscribe
   * needed on every render. */
  const handleScrub = useCallback((s: ElevationSample | null) => {
    const m = mapRef.current;
    if (m) setScrubPoint(m, s?.at ?? null);
  }, []);

  // Draw-mode keyboard shortcuts. Registered once; reads plannerRef so it
  // never closes over a stale planner. Bails out whenever a text input is
  // focused (the PlanBar save-name field) so typing there never undoes a
  // point or deletes a selection instead of editing the name.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = plannerRef.current;
      if (s.mode !== "draw") return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
        return;
      }
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        setPlanner(redo(plannerRef.current));
      } else if (meta && e.key.toLowerCase() === "z") {
        e.preventDefault();
        setPlanner(undo(plannerRef.current));
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (s.selected !== null) {
          e.preventDefault();
          setPlanner(deletePoint(s, s.selected, new Date().toISOString()));
        }
      } else if (e.key === "Escape") {
        if (waypointMenuRef.current !== null) {
          setWaypointMenu(null);
        } else if (s.selected !== null) {
          setPlanner(select(s, null));
        } else {
          requestClose();
        }
      } else if (e.key === "Enter") {
        // Same finish gesture as the map's dblclick handler — see its
        // comment in handleMapReady.
        if (s.plan.points.length >= 2) {
          e.preventDefault();
          setDoneNonce((n) => n + 1);
        }
      } else if (
        s.selected !== null &&
        mapRef.current &&
        (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight")
      ) {
        e.preventDefault();
        const map = mapRef.current;
        const at = s.plan.points[s.selected];
        const p = map.project(at);
        const delta = e.shiftKey ? 10 : 2;
        const dx = e.key === "ArrowLeft" ? -delta : e.key === "ArrowRight" ? delta : 0;
        const dy = e.key === "ArrowUp" ? -delta : e.key === "ArrowDown" ? delta : 0;
        const moved = map.unproject([p.x + dx, p.y + dy]);
        setPlanner(movePoint(s, s.selected, [moved.lng, moved.lat], new Date().toISOString()));
      }
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [requestClose]);

  // Map interactivity for draw mode: click to add/select, drag a control
  // point (the standard MapLibre "mousedown on a layer, temporary
  // mousemove/mouseup listeners" pattern), hover cursor. Registered once
  // per map instance; everything reads plannerRef so it always sees the
  // current mode/plan/selection.
  const handleMapReady = useCallback((map: maplibregl.Map) => {
    mapRef.current = map;
    // Mirrors the doubleClickZoom effect above, for the rare race where
    // draw mode starts before the map finishes loading (the PLAN control
    // is reachable before MapCanvas's Suspense boundary resolves) — by the
    // time that effect's own planner.mode dep would fire, mapRef.current
    // was still null. (Double-click itself is repurposed as "finish" in
    // the click handler below, not a separate "dblclick" listener — see
    // its comment.)
    if (plannerRef.current.mode === "draw") map.doubleClickZoom.disable();
    // Dev-only escape hatch for driving/inspecting the live map from the
    // console (never ships: DEV is compile-time false in production).
    if (import.meta.env.DEV) (window as unknown as { __map: maplibregl.Map }).__map = map;
    // ensureRouteOverlay returns false while the style is still loading —
    // and ALL of a load's "styledata" ticks can fire inside that window, so
    // event listeners alone can strand the overlay unpainted (map.once
    //("idle") has the same hazard: idle can fire before isStyleLoaded flips
    // and never re-fire on a quiet map). A plain timer poll has no ordering
    // assumptions: retry until the first successful paint, then stop; any
    // styledata/planner-change call re-primes it.
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const reEnsure = () => {
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (!ensureRouteOverlay(map, routeViewRef.current, routeLookRef.current, markingsRef.current)) {
        retryTimer = setTimeout(reEnsure, 400);
      }
    };
    reEnsureRef.current = reEnsure;
    map.on("styledata", reEnsure);
    map.on("remove", () => {
      if (retryTimer !== null) clearTimeout(retryTimer);
    });
    reEnsure();

    map.on("click", (e) => {
      const s = plannerRef.current;
      // The lift-off click of the long-press that just opened the menu —
      // swallow it once so the gesture doesn't self-dismiss (see the ref's
      // own comment).
      if (suppressTouchClickRef.current) {
        suppressTouchClickRef.current = false;
        return;
      }
      // A click anywhere on the map while the waypoint menu is open means
      // "never mind" — dismiss it rather than treating the click as a
      // point-add (docs' own "Escape/map-click closes it" contract).
      if (waypointMenuRef.current !== null) {
        setWaypointMenu(null);
        return;
      }
      if (s.mode !== "draw" || confirmDiscardRef.current || namingRef.current) return;
      // queryRenderedFeatures THROWS (not an empty result) when the layer
      // doesn't exist yet — a real possibility right after entering draw
      // mode, since ensureRouteOverlay's own layer creation and this click
      // handler race independently off the same map-ready/styledata
      // events. Treat "layer not there yet" as "nothing under the
      // cursor" so the click still falls through to addPoint instead of
      // silently losing the point.
      const pad = hitPad();
      const feats = map.getLayer(ROUTE_POINTS_LAYER)
        ? map.queryRenderedFeatures(
            [
              [e.point.x - pad, e.point.y - pad],
              [e.point.x + pad, e.point.y + pad],
            ],
            { layers: [ROUTE_POINTS_LAYER] },
          )
        : [];

      // Double-click-to-finish lives HERE, in click, rather than in its own
      // "dblclick" listener: the browser fires "click" once per click and
      // only THEN fires "dblclick" once more, so a double-click's own two
      // constituent clicks would each try to instant-add a point before
      // dblclick ever got a say — e.detail (the click's 1-based position in
      // that burst, set synchronously by the browser) is what lets a
      // repeat click recognize itself as one, unlike the hit-test above,
      // which lags a tile-worker round-trip behind React/map state and
      // can't reliably tell "the point THIS gesture's own first click just
      // added a moment ago" from "an unrelated point that was already
      // there."
      if (e.originalEvent.detail >= 2) {
        const addedJustNow = pendingClickAddRef.current;
        pendingClickAddRef.current = null;
        // A genuine double-click ON a pre-existing point is left alone —
        // not a finish gesture, and the first click already selected it.
        if (feats.length > 0 && !addedJustNow) return;
        // Undo whatever the first click of THIS gesture just committed —
        // a double-click means "the route is done AS DRAWN," not "add one
        // more point here, then finish."
        const base = addedJustNow && Date.now() - addedJustNow.at < 700 ? undo(s) : s;
        if (base !== s) setPlanner(base);
        if (base.plan.points.length >= 2) setDoneNonce((n) => n + 1);
        return;
      }

      const i = feats[0]?.properties?.i;
      if (typeof i === "number") {
        pendingClickAddRef.current = null;
        setPlanner(select(plannerRef.current, i));
        return;
      }

      const ghost = ghostRef.current;
      if (ghost !== null) {
        // Insert-on-line: the hover ghost was showing right here — this
        // click means "put a point ON the leg," not "extend the route
        // from its end" (docs/WEB_PLANNER_UX_REVIEW.md P1 #5 — today's
        // plain end-append is the #1 functional gap vs. CalTopo/Gaia/
        // RwGPS, which all support inserting mid-route).
        ghostRef.current = null;
        setPlanGhost(map, null);
        const router = routerRef.current;
        if (snapModeRef.current && router) {
          void (async () => {
            const hit = await router.snap(ghost.at);
            // The click's own continuation — bail if the user has moved
            // on (left draw mode, opened discard/naming/the waypoint menu)
            // while the snap was in flight, same guard as the plain-add
            // branch below.
            if (
              plannerRef.current.mode !== "draw" ||
              confirmDiscardRef.current ||
              namingRef.current ||
              waypointMenuRef.current !== null
            ) {
              return;
            }
            const finalAt = hit?.at ?? ghost.at;
            setPlanner(insertPoint(plannerRef.current, ghost.legIndex, finalAt, new Date().toISOString()));
            if (hit) flashSnap(map, hit.at);
          })();
        } else {
          setPlanner(insertPoint(plannerRef.current, ghost.legIndex, ghost.at, new Date().toISOString()));
        }
        return;
      }

      const clicked: LonLat = [e.lngLat.lng, e.lngLat.lat];
      const router = routerRef.current;
      if (snapModeRef.current && router) {
        // Magnetize: snap the click before it ever becomes a point, per
        // docs/WEB_PLANNER_UX_REVIEW.md's rage-list rule — a leg either
        // ends up honestly solid or honestly dashed, never a confident
        // wrong answer. Snap is 1-3 ranged reads (fast, no debounce), but
        // it's still async, so guard the continuation the same way as the
        // insert branch above.
        void (async () => {
          const hit = await router.snap(clicked);
          if (
            plannerRef.current.mode !== "draw" ||
            confirmDiscardRef.current ||
            namingRef.current ||
            waypointMenuRef.current !== null
          ) {
            return;
          }
          const finalAt = hit?.at ?? clicked;
          pendingClickAddRef.current = { at: Date.now() };
          setPlanner(select(addPoint(plannerRef.current, finalAt, new Date().toISOString()), null));
          if (hit) flashSnap(map, hit.at);
        })();
      } else {
        pendingClickAddRef.current = { at: Date.now() };
        setPlanner(select(addPoint(plannerRef.current, clicked, new Date().toISOString()), null));
      }
    });

    // Right-click a control point deletes it outright — the on-screen
    // answer to "how do I delete a point without a keyboard." Right-click
    // a waypoint removes IT instead (control-point delete keeps priority
    // when both would somehow hit — they're separate layers, so only a
    // near-exact overlap could). Right-click empty space opens the
    // "add a waypoint here" menu — previously a no-op there, so this now
    // ALWAYS handles draw-mode right-click and always preventDefaults,
    // where before only the delete-a-point path did. Bound to the
    // canvas's NATIVE "contextmenu" DOM event rather than maplibre's own
    // map.on("contextmenu", …) wrapper: maplibre gates that behind an
    // internal mousedown/mouseup state machine (MapEventHandler's
    // _delayContextMenu/_ignoreContextMenu, verified against its source)
    // meant to coalesce the OS's own right-click sequence — going straight
    // to the DOM event sidesteps that entirely and is the same amount of
    // code. Same padded-bbox hit-test as click/startDrag above (the
    // layer-scoped map.on("contextmenu", ROUTE_POINTS_LAYER, …) form
    // hit-tests with a bare, zero-padding point and skips the listener
    // entirely on a near-miss — pixel-exact precision is the tax this
    // whole wave exists to remove).
    map.getCanvas().addEventListener("contextmenu", (e) => {
      const s = plannerRef.current;
      if (s.mode !== "draw" || confirmDiscardRef.current || namingRef.current) return;
      e.preventDefault(); // the DOM method — suppresses the native right-click menu
      const rect = map.getCanvas().getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const bbox: [[number, number], [number, number]] = [
        [x - 6, y - 6],
        [x + 6, y + 6],
      ];

      const pointFeats = map.getLayer(ROUTE_POINTS_LAYER)
        ? map.queryRenderedFeatures(bbox, { layers: [ROUTE_POINTS_LAYER] })
        : [];
      const pi = pointFeats[0]?.properties?.i;
      if (typeof pi === "number") {
        setWaypointMenu(null); // any stale open menu from an earlier right-click
        setPlanner(deletePoint(plannerRef.current, pi, new Date().toISOString()));
        return;
      }

      const waypointFeats = map.getLayer(ROUTE_WAYPOINTS_LAYER)
        ? map.queryRenderedFeatures(bbox, { layers: [ROUTE_WAYPOINTS_LAYER] })
        : [];
      const wi = waypointFeats[0]?.properties?.i;
      if (typeof wi === "number") {
        setWaypointMenu(null);
        setPlanner(removeWaypoint(plannerRef.current, wi, new Date().toISOString()));
        return;
      }

      // Empty map: open (or reposition) the kind picker at the click,
      // storing its lngLat now so a kind pick later doesn't need to
      // re-derive it. clearGhost is declared further down this same
      // closure but only runs later, off this async DOM event, by which
      // point the whole handler body below has long since finished
      // executing — an ordinary closure-over-a-later-const, not a hazard.
      clearGhost();
      const { lng, lat } = map.unproject([x, y]);
      setWaypointMenu({ at: [lng, lat], x: e.clientX, y: e.clientY });
    });

    // The touch route to the same menu — phones have no right-click, and
    // until this existed the whole waypoint vocabulary (Campsite, Gate,
    // Water crossing, …) was desktop-only (Lee's field ask, Aug 25). A
    // 500 ms still press on EMPTY map opens the kind picker; a press ON A
    // WAYPOINT removes it — the exact touch mirror of desktop's
    // right-click-on-waypoint delete below. A press on a CONTROL POINT is
    // deliberately left to the tap's own select flow — mirroring
    // right-click's instant point-delete under a finger would make
    // fat-finger deletes routine (unlike a waypoint marker, which is
    // small, sparse, and never the thing a stray long-press is most
    // likely to land on while just trying to select/drag the route line).
    // Movement past ~8px or a second finger cancels (that's a pan/pinch,
    // not a press).
    const canvas = map.getCanvas();
    let pressTimer: ReturnType<typeof setTimeout> | null = null;
    let pressAt: { x: number; y: number } | null = null;
    const cancelPress = () => {
      if (pressTimer !== null) {
        clearTimeout(pressTimer);
        pressTimer = null;
      }
    };
    canvas.addEventListener(
      "touchstart",
      (e) => {
        cancelPress();
        if (e.touches.length !== 1) return;
        const s = plannerRef.current;
        if (s.mode !== "draw" || confirmDiscardRef.current || namingRef.current) return;
        const t = e.touches[0];
        const rect = canvas.getBoundingClientRect();
        pressAt = { x: t.clientX - rect.left, y: t.clientY - rect.top };
        const client = { x: t.clientX, y: t.clientY };
        pressTimer = setTimeout(() => {
          pressTimer = null;
          const { x, y } = pressAt!;
          const pad = hitPad();
          const bbox: [[number, number], [number, number]] = [
            [x - pad, y - pad],
            [x + pad, y + pad],
          ];
          // Same throws-when-layer-missing caveat as the click handler's
          // hit-test — treat "not there yet" as "not hit." Checked as two
          // separate layer queries (not one combined .some, like before)
          // since the waypoint branch below needs to know WHICH layer hit,
          // not just whether one did — same two-step shape as the desktop
          // contextmenu handler above.
          const onPoint = map.getLayer(ROUTE_POINTS_LAYER)
            ? map.queryRenderedFeatures(bbox, { layers: [ROUTE_POINTS_LAYER] }).length > 0
            : false;
          if (onPoint) return; // control point: the tap's own select flow owns it

          const waypointFeats = map.getLayer(ROUTE_WAYPOINTS_LAYER)
            ? map.queryRenderedFeatures(bbox, { layers: [ROUTE_WAYPOINTS_LAYER] })
            : [];
          const wi = waypointFeats[0]?.properties?.i;
          suppressTouchClickRef.current = true;
          if (typeof wi === "number") {
            // Touch mirror of the desktop contextmenu handler's
            // removeWaypoint branch above — same suppress-next-click flag
            // so the finger lift's synthetic click doesn't also act.
            setPlanner(removeWaypoint(plannerRef.current, wi, new Date().toISOString()));
            return;
          }
          clearGhost();
          const { lng, lat } = map.unproject([x, y]);
          setWaypointMenu({ at: [lng, lat], x: client.x, y: client.y });
        }, 500);
      },
      { passive: true },
    );
    canvas.addEventListener(
      "touchmove",
      (e) => {
        if (pressTimer === null || pressAt === null) return;
        if (e.touches.length !== 1) {
          cancelPress();
          return;
        }
        const t = e.touches[0];
        const rect = canvas.getBoundingClientRect();
        const dx = t.clientX - rect.left - pressAt.x;
        const dy = t.clientY - rect.top - pressAt.y;
        if (dx * dx + dy * dy > 64) cancelPress();
      },
      { passive: true },
    );
    canvas.addEventListener("touchend", cancelPress, { passive: true });
    canvas.addEventListener("touchcancel", cancelPress, { passive: true });

    map.on("mouseenter", ROUTE_POINTS_LAYER, () => {
      if (plannerRef.current.mode === "draw" && dragIndexRef.current === null) {
        map.getCanvas().style.cursor = "grab";
      }
    });
    map.on("mouseleave", ROUTE_POINTS_LAYER, () => {
      if (dragIndexRef.current === null) map.getCanvas().style.cursor = "";
    });

    const clearGhost = () => {
      if (ghostRef.current !== null) {
        ghostRef.current = null;
        setPlanGhost(map, null);
      }
    };

    // Insert-on-line hover preview (docs/WEB_PLANNER_UX_REVIEW.md P1 #5):
    // while drawing, not over a control point and not mid-drag, throttle
    // to ~30 ms and hit-test the cursor against every leg's geometry —
    // within 12 px of one shows a ghost point AT the nearest spot on that
    // leg (nearestOnLeg's whole reason for existing over nearestLeg) and
    // switches the cursor to "copy"; the click handler above reads
    // ghostRef to turn a click into insertPoint instead of a plain append.
    let lastGhostMove = 0;
    map.on("mousemove", (e) => {
      const s = plannerRef.current;
      if (
        s.mode !== "draw" ||
        confirmDiscardRef.current ||
        namingRef.current ||
        dragIndexRef.current !== null ||
        waypointMenuRef.current !== null
      ) {
        return;
      }
      const now = performance.now();
      if (now - lastGhostMove < 30) return;
      lastGhostMove = now;

      const overPoint = map.getLayer(ROUTE_POINTS_LAYER)
        ? map.queryRenderedFeatures(
            [
              [e.point.x - 6, e.point.y - 6],
              [e.point.x + 6, e.point.y + 6],
            ],
            { layers: [ROUTE_POINTS_LAYER] },
          ).length > 0
        : false;
      if (overPoint) {
        clearGhost();
        return;
      }

      const cursorAt: LonLat = [e.lngLat.lng, e.lngLat.lat];
      const hit = nearestOnLeg(s.plan.points, s.plan.legs, cursorAt);
      // Web Mercator meters-per-pixel at this latitude/zoom — the standard
      // formula (156543.03 m/px at zoom 0, equator), so "within 12 px"
      // reads the same regardless of how far zoomed in the map is.
      const metersPerPixel = (156543.03 * Math.cos((cursorAt[1] * Math.PI) / 180)) / 2 ** map.getZoom();
      if (hit && hit.distM <= 12 * metersPerPixel) {
        ghostRef.current = { legIndex: hit.legIndex, at: hit.at };
        setPlanGhost(map, hit.at);
        map.getCanvas().style.cursor = "copy";
      } else {
        clearGhost();
        map.getCanvas().style.cursor = "";
      }
    });
    // Cursor leaving the canvas entirely — mousemove alone never fires for
    // this, so the ghost would otherwise linger at its last position.
    map.on("mouseout", () => clearGhost());

    const startDrag = (e: maplibregl.MapLayerMouseEvent | maplibregl.MapLayerTouchEvent) => {
      if (
        plannerRef.current.mode !== "draw" ||
        confirmDiscardRef.current ||
        namingRef.current ||
        waypointMenuRef.current !== null
      ) {
        return;
      }
      const i = e.features?.[0]?.properties?.i;
      if (typeof i !== "number") return;
      e.preventDefault();
      clearGhost();
      map.dragPan.disable();
      map.getCanvas().style.cursor = "grabbing";
      dragIndexRef.current = i;
      dragOriginRef.current = plannerRef.current;
      setPlanner(select(plannerRef.current, i));

      let moved = false;
      const onMove = (moveEvent: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
        moved = true;
        const origin = dragOriginRef.current;
        if (origin !== null) {
          setPlanner(previewPoint(origin, i, [moveEvent.lngLat.lng, moveEvent.lngLat.lat]));
        }
      };
      const onUp = (upEvent: maplibregl.MapMouseEvent | maplibregl.MapTouchEvent) => {
        map.off("mousemove", onMove);
        map.off("touchmove", onMove);
        map.off("mouseup", onUp);
        map.off("touchend", onUp);
        const origin = dragOriginRef.current;
        dragOriginRef.current = null;
        dragIndexRef.current = null;
        map.dragPan.enable();
        map.getCanvas().style.cursor = plannerRef.current.mode === "draw" ? "grab" : "";
        // A plain click (mousedown+mouseup, no mousemove between) already
        // selected the point above — committing a no-op movePoint here
        // would push a spurious undo step for a drag that never happened.
        if (origin === null || !moved) return;
        const dropped: LonLat = [upEvent.lngLat.lng, upEvent.lngLat.lat];
        const router = routerRef.current;
        if (snapModeRef.current && router) {
          // Drag-commit re-solve: try snapping the dropped coordinate;
          // the adjacent legs were already degraded to straight by
          // movePoint/previewPoint's degradeAdjacentLegs, so once this
          // commits, the leg-solver effect picks them up on its own
          // (their endpoint(s) changed -> a fresh key).
          void (async () => {
            const hit = await router.snap(dropped);
            if (
              plannerRef.current.mode !== "draw" ||
              confirmDiscardRef.current ||
              namingRef.current ||
              waypointMenuRef.current !== null
            ) {
              return;
            }
            const finalAt = hit?.at ?? dropped;
            setPlanner(movePoint(origin, i, finalAt, new Date().toISOString()));
            if (hit) flashSnap(map, hit.at);
          })();
        } else {
          setPlanner(movePoint(origin, i, dropped, new Date().toISOString()));
        }
      };
      map.on("mousemove", onMove);
      map.on("touchmove", onMove);
      map.on("mouseup", onUp);
      map.on("touchend", onUp);
    };
    map.on("mousedown", ROUTE_POINTS_LAYER, startDrag);
    map.on("touchstart", ROUTE_POINTS_LAYER, startDrag);
  }, []);

  const load = useCallback(async (wantRegion?: string) => {
    setPhase({ kind: "loading" });
    try {
      const states = await fetchStates();
      let state =
        states.find((s) => s.region === wantRegion) ??
        states.find((s) => s.region === parseHash(location.hash).state);
      if (!state) {
        // one quick geolocation attempt; fall back to Utah
        const pos = await new Promise<GeolocationPosition | null>((resolve) => {
          if (!("geolocation" in navigator)) return resolve(null);
          navigator.geolocation.getCurrentPosition(
            (p) => resolve(p),
            () => resolve(null),
            { timeout: 3000, maximumAge: 600000 },
          );
        });
        if (pos) {
          state =
            stateContaining(states, pos.coords.longitude, pos.coords.latitude) ?? undefined;
        }
      }
      state ??= states.find((s) => s.region === "utah") ?? states[0];
      const manifest = await fetchManifest(state.region);
      setPhase({ kind: "ready", states, state, manifest });
    } catch (e) {
      setPhase({
        kind: "error",
        message: e instanceof Error ? e.message : String(e),
        retry: () => void load(wantRegion),
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** A GPX file handed up from PlansSheet's IMPORT GPX, or a decoded share
   * payload from the open-path effect below — either way, enters draw mode
   * with it exactly like resuming a draft (editingId null: it isn't any
   * saved plan, so DONE saves it as new), frames the map over it, and —
   * the one import-specific step — forces SNAP off for this session
   * first. Without that, the leg-solver effect would see a long imported
   * track's every straight leg as eligible in the same settle tick and
   * start burning through CONCURRENT_SOLVE_CAP-at-a-time solves the
   * instant the plan lands; the user turns SNAP on deliberately once
   * they're ready to watch it happen. Hoisted to a stable useCallback
   * (its ready-phase siblings below — handleOpenPlan, handleDone, etc. —
   * stay plain consts) so the share open-path effect, which must be
   * registered ahead of those early returns, can call the very same
   * function rather than a duplicate. */
  const handleImportPlan = useCallback((plan: Plan) => {
    setShowPlans(false);
    setSnapMode(false);
    setPlanner({ mode: "draw", plan, editingId: null, selected: null, undo: [], redo: [] });
    setTimeout(() => {
      if (mapRef.current) fitBoundsToPoints(mapRef.current, plan.points);
    }, 0);
  }, []);

  // Share open-path (docs/SHARE_LINK_SPEC.md): a /p link, or any hash that
  // happens to decode as DLRP1 regardless of path (a defensive fallback —
  // e.g. a bare "/#<payload>" that bypassed the _redirects rewrite), loads
  // its region and hands the plan through the SAME import flow a GPX drop
  // uses. decodeShareFragment runs on the RAW hash captured in
  // pendingShareRef, never through parseHash's k=v reading — a compressed
  // payload isn't state=/look=/at= data (parseHash's own read of it is
  // harmless, just useless: no '&'/'=' survive base64url, so it yields
  // {}). Gated on "ready" since handleImportPlan needs mapRef/setPlanner;
  // pendingShareRef is nulled on the FIRST attempt (success or failure),
  // so this is a genuine no-op on every later phase change (a state
  // switch, e.g.) rather than replaying a stale payload.
  useEffect(() => {
    if (phase.kind !== "ready") return;
    const { state, states } = phase;
    const pending = pendingShareRef.current;
    if (!pending) return;
    pendingShareRef.current = null;
    const { hash, isSharePath } = pending;
    void decodeShareFragment(hash, new Date().toISOString()).then((plan) => {
      if (!plan) {
        // Only a /p path signals genuine share INTENT — an ordinary load
        // whose hash happens not to decode (nearly every load, in
        // practice) must stay silent here.
        if (isSharePath) showToast("Couldn't read that shared route");
        return;
      }
      const openShared = () => {
        handleImportPlan(plan);
        // Strip the payload — and the now-dead-end /p path — from the URL
        // bar. The plan is a draft from here; leaving the payload in
        // place would re-import it over newer edits on the next reload.
        // The "every view is a link" effect below re-fills the hash with
        // #state=…&look=…&at= on its own next tick.
        history.replaceState(null, "", isSharePath ? "/" : location.pathname);
        showToast("Shared route loaded");
      };
      if (plan.region === state.region) {
        openShared(); // already on the right region — skip the reload flash
      } else if (states.some((s) => s.region === plan.region)) {
        void load(plan.region).then(openShared);
      } else {
        showToast("This route's state isn't served yet");
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Every view is a link: #state=…&look=…&at=lon,lat,zoom
  useEffect(() => {
    if (phase.kind !== "ready") return;
    const at = camera
      ? `${camera.lon.toFixed(5)},${camera.lat.toFixed(5)},${camera.zoom.toFixed(2)}`
      : "";
    history.replaceState(
      null,
      "",
      buildHash({ state: phase.state.region, look, at }),
    );
  }, [phase, look, camera]);

  useEffect(() => {
    try {
      localStorage.setItem("layers", JSON.stringify(toggles));
    } catch {
      /* private mode — settings just don't persist */
    }
  }, [toggles]);

  // ⌘K / ctrl-K toggles search (through openPanel so it can never stack
  // over another sheet)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        openPanelRef.current(showSearchRef.current ? null : "search");
      }
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, []);

  if (phase.kind === "loading") {
    return (
      <div className="fullscreen-center">
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }
  if (phase.kind === "error") {
    return (
      <div className="fullscreen-center">
        <div className="error-card" role="alert">
          <h1>Couldn't reach the map catalog</h1>
          <p>{phase.message}</p>
          <button onClick={phase.retry}>Try again</button>
        </div>
      </div>
    );
  }

  const { states, state, manifest } = phase;
  const switchState = (region: string) => {
    setShowStates(false);
    setCamera(undefined);
    void load(region);
  };

  // ------------------------------------------------------- planner: ready-
  // phase handlers (need `state`/`load`, so they live below the early
  // returns — same reason switchState does).
  const handleNewRoute = () => {
    setShowPlans(false);
    setPlanner(start(state.region, new Date().toISOString()));
  };

  const handleResumeDraft = (draft: Plan) => {
    setShowPlans(false);
    // store.ts's draft slot is a bare Plan (no id) — a resumed draft that
    // WAS editing a saved plan can't remember which one; DONE will save it
    // as a new plan rather than silently overwriting the original.
    setPlanner({ mode: "draw", plan: draft, editingId: null, selected: null, undo: [], redo: [] });
  };

  const handleOpenPlan = async (saved: SavedPlan) => {
    setShowPlans(false);
    if (saved.region !== state.region) await load(saved.region);
    setPlanner(loadPlan(saved));
    // Give the state-switch effects (MapCanvas's own setStyle/jumpTo, fired
    // off the phase/state change above) a tick to land before framing —
    // otherwise a same-tick fitBounds can race a jumpTo that hasn't run
    // yet and lose the framing to it.
    setTimeout(() => {
      if (mapRef.current) fitBoundsToPoints(mapRef.current, saved.points);
    }, 0);
  };

  /** PlansSheet's SHARE button, once encodeShareURL resolves — App owns
   * the ShareSheet overlay so opening it can enforce the one-overlay rule
   * the same way every other PlansSheet handler already does. */
  const handleShare = (payload: { plan: SavedPlan; url: string }) => {
    setShowPlans(false);
    setShare(payload);
  };

  const handleDone = (name: string) => {
    const finalPlan: Plan = { ...planner.plan, name, updatedAt: new Date().toISOString() };
    savePlan(finalPlan, stats, planner.editingId ?? undefined);
    saveDraft(null);
    setPlanner(exit(planner));
    showToast("SAVED");
    setAnnounce("Route saved");
  };

  /** PlanBar's context-row day-break toggle. An endpoint (first/last
   * control point, or nothing selected) can't host an overnight — matches
   * toggleDayBreak's own guard in plannerCore.ts — so PlanBar hides the
   * button entirely rather than showing one that would just no-op. */
  const dayBreakState: "none" | "set" | "ineligible" =
    planner.selected !== null && planner.selected > 0 && planner.selected < planner.plan.points.length - 1
      ? (planner.plan.dayBreaks ?? []).includes(planner.selected)
        ? "set"
        : "none"
      : "ineligible";

  // The waypoint menu's clamped screen position — see clampMenuPos's own
  // doc above. Recomputed from the raw click/touch point on every render
  // rather than at open time, so it also stays correct if the viewport
  // itself changes size while the menu is open (a rotation, mainly) — same
  // reasoning extends to the taller coarse-pointer estimate below, which
  // wouldn't otherwise re-check if a hybrid device's pointer type changed
  // between the long-press that opened this and this render.
  const waypointMenuH = matchMedia("(pointer: coarse)").matches ? WAYPOINT_MENU_H_COARSE : WAYPOINT_MENU_H;
  const waypointMenuPos = waypointMenu
    ? clampMenuPos(waypointMenu.x + 4, waypointMenu.y + 4, WAYPOINT_MENU_W, waypointMenuH, WAYPOINT_MENU_MARGIN)
    : null;

  return (
    <div className={`app ${night ? "night" : ""} ${planner.mode === "draw" ? "drawing" : ""}`}>
      <Suspense fallback={<div className="fullscreen-center"><div className="spinner" aria-label="Loading map" /></div>}>
        <MapCanvas
          state={state}
          manifest={manifest}
          look={look}
          night={night}
          toggles={toggles}
          markings={markings}
          showUTMGrid={showUTMGrid}
          sun={sun}
          camera={camera}
          onCamera={setCamera}
          onMapReady={handleMapReady}
        />
      </Suspense>
      <header className="hud-rail">
        <button
          className="pill"
          onClick={() => openPanel("states")}
          disabled={planner.mode === "draw"}
          title="Choose state"
        >
          {state.name.toUpperCase()}
        </button>
        <span className="pill pill-static" title="Map data built">
          BUILT {manifest.built ?? state.built}
        </span>
        <span className="spacer" />
        <button className="pill" onClick={() => openPanel("search")} title="Search (⌘K)">
          SEARCH ⌘K
        </button>
      </header>
      <aside className="controls">
        {/* Disabled mid-draw for the same reason as the state pill: NEW
          * ROUTE / open-another-plan from the sheet would silently replace
          * the live drawing. */}
        <button
          className="control control-plan"
          onClick={() => openPanel("plans")}
          disabled={planner.mode === "draw"}
          title="Plan a route"
        >
          <span className="control-label">PLAN</span>
          {hasDraft && <span className="control-badge" aria-hidden="true" />}
        </button>
        <div className="control-look-wrap">
          <button
            className="control"
            onClick={() => {
              // The click that follows a long-press's own pointerup —
              // suppressed so opening the menu doesn't ALSO cycle the look.
              if (suppressLookClickRef.current) {
                suppressLookClickRef.current = false;
                return;
              }
              setLook(nextLook(look, manifest));
            }}
            onPointerDown={() => {
              clearLookPressTimer();
              lookPressTimerRef.current = setTimeout(() => {
                lookPressTimerRef.current = null;
                suppressLookClickRef.current = true;
                setShowLookMenu(true);
              }, LOOK_LONG_PRESS_MS);
            }}
            onPointerUp={clearLookPressTimer}
            onPointerLeave={clearLookPressTimer}
            onContextMenu={(e) => {
              // Right-click (and a touch long-press, which browsers
              // synthesize into a contextmenu of their own) opens the menu
              // directly — preventDefault covers both the desktop menu and
              // the mobile callout.
              e.preventDefault();
              clearLookPressTimer();
              setShowLookMenu(true);
            }}
            title="Map look — hold for all"
            aria-haspopup="true"
            aria-expanded={showLookMenu}
          >
            <span className="control-label">{LOOK_LABELS[look].toUpperCase()}</span>
          </button>
          {showLookMenu && (
            <LookMenu
              looks={availableLooks(manifest)}
              current={look}
              onPick={(l) => {
                setLook(l);
                closeLookMenu();
              }}
              onClose={closeLookMenu}
              routeLook={routeLook}
              onRouteLookChange={setRouteLook}
              markings={markings}
              onMarkingsChange={setMarkings}
            />
          )}
        </div>
        <button className="control" onClick={() => openPanel("layers")} title="Layers">
          <span className="control-label">LAYERS</span>
        </button>
      </aside>
      <div className="plan-dock">
        {/* Always mounted (even in browse) so a "Route saved" announcement
          * fired from handleDone — which flips mode back to browse in the
          * SAME update — has somewhere to land: a live region that unmounts
          * the instant its text changes never actually gets read out. It's
          * empty and pointer-events:none-by-default the rest of the time,
          * so this is a no-op visually outside draw mode. */}
        <div className="sr-only" role="status">
          {announce}
        </div>
        {planner.mode === "draw" && (
          <>
            {profileOpen && (
              <ProfileStrip
                samples={samples}
                sampling={sampling}
                dayMarks={dayMarks}
                onScrub={handleScrub}
              />
            )}
            <PlanBar
              plan={planner.plan}
              stats={stats}
              sampling={sampling}
              days={days}
              canUndo={planner.undo.length > 0}
              canRedo={planner.redo.length > 0}
              onUndo={() => setPlanner(undo(planner))}
              onRedo={() => setPlanner(redo(planner))}
              profileOpen={profileOpen}
              onToggleProfile={() => setProfileOpen((v) => !v)}
              selected={planner.selected}
              onDeleteSelected={() => {
                if (planner.selected !== null) {
                  setPlanner(deletePoint(planner, planner.selected, new Date().toISOString()));
                }
              }}
              dayBreakState={dayBreakState}
              onToggleDayBreak={() => {
                if (planner.selected !== null) {
                  setPlanner(toggleDayBreak(plannerRef.current, planner.selected, new Date().toISOString()));
                }
              }}
              confirmDiscard={confirmDiscard}
              onRequestClose={requestClose}
              onConfirmDiscard={() => {
                saveDraft(null);
                setPlanner(exit(planner));
                setConfirmDiscard(false);
              }}
              onCancelDiscard={() => setConfirmDiscard(false)}
              onDone={handleDone}
              onNamingChange={(v) => (namingRef.current = v)}
              requestDoneNonce={doneNonce}
              snapMode={snapMode}
              snapAvailable={router !== null}
              onToggleSnap={() => setSnapMode((v) => !v)}
              solving={solving > 0}
              longSolving={longSolving > 0}
              onReverse={() => setPlanner(reverse(planner, new Date().toISOString()))}
              onOutAndBack={() => setPlanner(outAndBack(planner, new Date().toISOString()))}
              closures={closures}
              gradeOn={gradeOn}
              gradeAvailable={samples.length >= 2}
              onToggleGrade={() => setGradeOn((v) => !v)}
              suggestedName={suggestedName}
              fuelGapM={fuelGapM}
              onOpenItinerary={() => setShowItinerary(true)}
            />
          </>
        )}
      </div>
      {showLayers && (
        <LayersPanel
          toggles={toggles}
          manifest={manifest}
          look={look}
          night={night}
          onChange={setToggles}
          onClose={() => setShowLayers(false)}
        />
      )}
      {showStates && (
        <StatePicker states={states} current={state} onPick={switchState} onClose={() => setShowStates(false)} />
      )}
      {showSearch && (
        <SearchPalette
          state={state}
          onClose={() => setShowSearch(false)}
          onJump={(lon, lat, zoom) => {
            // camera state alone never reaches the map (MapCanvas reads it
            // only at construction) — fly the live map itself; moveend
            // keeps the hash in sync.
            setCamera({ lon, lat, zoom });
            mapRef.current?.flyTo({ center: [lon, lat], zoom, duration: 1200 });
            setShowSearch(false);
          }}
        />
      )}
      {showPlans && (
        <PlansSheet
          onClose={() => setShowPlans(false)}
          onNewRoute={handleNewRoute}
          onResumeDraft={handleResumeDraft}
          onOpenPlan={handleOpenPlan}
          onImportPlan={handleImportPlan}
          onShare={handleShare}
          region={state.region}
          openPlanId={planner.mode === "draw" ? planner.editingId : null}
          openPlanSamples={planner.mode === "draw" ? samples : null}
        />
      )}
      <ShareSheet share={share} onClose={() => setShare(null)} />
      {showItinerary && (
        <ItinerarySheet
          plan={planner.plan}
          days={days}
          onNotesChange={(notes) =>
            setPlanner((s) => ({
              ...s,
              plan: { ...s.plan, dayNotes: notes, updatedAt: new Date().toISOString() },
            }))
          }
          onClose={() => setShowItinerary(false)}
        />
      )}
      {waypointMenu && waypointMenuPos && (
        <div
          className="anchor-menu waypoint-menu"
          style={{ left: waypointMenuPos.x, top: waypointMenuPos.y }}
        >
          <div className="anchor-menu-title">WAYPOINT</div>
          {WAYPOINT_KINDS.map((kind, i) => (
            <button
              key={kind}
              ref={i === 0 ? waypointMenuFirstBtnRef : undefined}
              className="anchor-menu-btn"
              onClick={() => {
                setPlanner(addWaypoint(plannerRef.current, waypointMenu.at, kind, new Date().toISOString()));
                setWaypointMenu(null);
              }}
            >
              {kind}
            </button>
          ))}
          <button
            className="anchor-menu-btn anchor-menu-btn-cancel"
            onClick={() => setWaypointMenu(null)}
          >
            CANCEL
          </button>
        </div>
      )}
      {toast && <div className="toast">{toast}</div>}
      <footer className="colophon">
        <span>© Dustlark · built for zero bars</span>
        <span>{__APP_VERSION__}</span>
      </footer>
    </div>
  );
}

/** Night follows the system; a manual toggle can come later. */
function useNight(): boolean {
  const [night, setNight] = useState(
    () => matchMedia("(prefers-color-scheme: dark)").matches,
  );
  useEffect(() => {
    const mq = matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setNight(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return night;
}

/** Live sun position for the outdoors look's hillshade — recomputed on
 * state switch and every 10 minutes. The state's bbox center is close
 * enough (azimuth/altitude barely move across a state-sized area); this
 * deliberately does NOT track the live camera, so panning/zooming never
 * forces a style rebuild. useMemo (not an effect) so the very first style
 * MapCanvas builds already has a real sun position, not a null-then-swap. */
function useLiveSun(state: StateEntry | null): SunPosition | null {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), SUN_REFRESH_MS);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    if (!state) return null;
    const [lon, lat] = bboxCenter(state);
    return solarPosition(new Date(), lat, lon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.region, tick]);
}
